/* ==========================================================================
   Product gallery slider — tablet & desktop ( >= 750px )

   Uses Embla Carousel, the same engine as the Featured products / Sets
   sections, instead of the theme's own gallery scripts.

   Why replace the theme's gallery here:
     • SliderComponent (global.js) pages by writing `scrollLeft` and counts
       slides with `clientWidth > 0`. On desktop the theme lays out ONE slide
       (`.product--thumbnail_slider .product__media-item:not(.is-active)
       {display:none}`), so there was nothing to scroll and the arrows did
       nothing at all.
     • media-gallery.js's setActiveMedia() ends up in announceLiveRegion(),
       whose last line is `image.src = image.src`. That makes the browser drop
       the decoded image and re-fetch it, so the frame went blank on the first
       click and only appeared on the second (from cache).

   Embla owns the transform, so neither of those paths runs. The theme's
   markup is reused as-is: <ul.product__media-list> becomes the Embla container
   and each <li.product__media-item> a slide.

   Mobile (< 750px) is untouched — there the media list is a real horizontal
   scroller styled in new-design-product.css and it works as intended.
   ========================================================================== */
(() => {
  'use strict';

  // Dot pitch and window size must match the CSS (mirrors Sets / Featured).
  const DOT_STEP = 12;
  const DOT_WINDOW = 5;

  class ProductGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      this.rail = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!this.viewer) return;

      this.track = this.viewer.querySelector('.product__media-list');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.product__media-item'));
      if (this.slides.length < 2) return;

      this.prev = this.viewer.querySelector('button[name="previous"]');
      this.next = this.viewer.querySelector('button[name="next"]');

      // Embla now drives both breakpoints — mobile is a swipeable carousel with
      // dots (design "Page control"), desktop adds the thumbnail rail and
      // arrows. Only the controls differ, so there is nothing to re-mount on
      // resize; `isDesktop()` is consulted where behaviour diverges.
      this.desktopMql = window.matchMedia('(min-width: 750px)');
      this.buildDots();
      this.mount();
    }

    isDesktop() {
      return this.desktopMql.matches;
    }

    /* ---- lifecycle ---------------------------------------------------- */

    mount() {
      if (this.embla || typeof window.EmblaCarousel === 'undefined') return;

      // Embla needs a viewport (overflow hidden) wrapping the flex container.
      // The theme gives us the <ul> only, so the <slider-component> around it
      // plays the viewport role — marked with a class the CSS keys off.
      this.viewer.classList.add('is-embla');
      this.track.classList.add('is-embla-track');

      // Native image drag would fight the carousel drag — same as Sets.
      this.viewer.querySelectorAll('img').forEach((img) => {
        img.setAttribute('draggable', 'false');
      });

      // Options identical to the Sets / Featured galleries. `container` is the
      // only addition: those sections put the track first inside the viewport,
      // whereas the theme's snippet starts with a skip-to-content link, so
      // Embla's default (children[0]) would pick the wrong element.
      this.embla = window.EmblaCarousel(this.viewer, {
        container: this.track,
        loop: false,
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: false,
        // Snappier arrow/dot transitions (Embla: higher = slower, range 20–60).
        duration: 20,
      });

      // The theme's own handlers would fight us: SliderComponent scrolls, and
      // media-gallery re-assigns img.src. Intercept in the capture phase.
      this.bindArrow(this.prev, -1);
      this.bindArrow(this.next, 1);
      this.bindRail();
      this.bindRailDrag();

      this.embla.on('select', () => this.sync());
      this.sync();

      // The image box has no height until the photo has loaded, and it changes
      // on resize — recompute the rail cap in both cases.
      if (this.rail) {
        this.viewer.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', () => this.syncRailHeight(), { once: true });
        });
        window.addEventListener('resize', () => this.syncRailHeight());
      }
    }

    /* ---- controls ------------------------------------------------------ */

    /* Dots (design "Page control"). The theme's markup has none, so they're
       created here and reused on both breakpoints — CSS decides where to show
       them (mobile only, per the design). Same sliding-window behaviour as the
       Sets / Featured galleries: with more than DOT_WINDOW images every dot is
       rendered but the row is translated so the active one rides a fixed
       5-slot window, and data-rank scales the outer dots down. */
    buildDots() {
      const wrap = document.createElement('div');
      wrap.className = 'new-design-product__dots';
      wrap.setAttribute('aria-hidden', 'true');
      this.viewer.appendChild(wrap);
      this.dotsWrap = wrap;

      this.dots = this.slides.map((_, index) => {
        const dot = document.createElement('span');
        dot.className = 'new-design-product__dot';
        dot.addEventListener('click', () => this.embla?.scrollTo(index));
        wrap.appendChild(dot);
        return dot;
      });

      wrap.classList.toggle('new-design-product__dots--windowed', this.dots.length > DOT_WINDOW);
    }

    syncDots(selected) {
      if (!this.dots || !this.dots.length) return;
      const count = this.dots.length;

      if (count <= DOT_WINDOW) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-selected', i === selected);
          dot.removeAttribute('data-rank');
        });
        this.dotsWrap.style.removeProperty('--dots-shift');
        return;
      }

      const half = Math.floor(DOT_WINDOW / 2);
      const centre = Math.min(Math.max(selected, half), count - 1 - half);
      this.dotsWrap.style.setProperty('--dots-shift', `${-(centre - half) * DOT_STEP}px`);

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-selected', i === selected);
        const dist = Math.abs(i - centre);
        dot.dataset.rank = dist > half ? 'out' : String(dist);
      });
    }

    bindArrow(button, step) {
      if (!button) return;
      button.addEventListener(
        'click',
        (event) => {
          if (!this.embla) return;
          event.preventDefault();
          event.stopPropagation();
          if (step > 0) this.embla.scrollNext();
          else this.embla.scrollPrev();
        },
        true
      );
    }

    /* Drag the thumbnail rail up and down with the mouse.
       Pointer Events cover mouse, pen and touch in one path; touch keeps the
       browser's own scrolling (touch-action: pan-y in the CSS) because native
       momentum feels better than anything we'd emulate.
       A drag past DRAG_SLOP suppresses the click that follows, so dragging the
       strip never selects whatever thumbnail the pointer happened to land on. */
    bindRailDrag() {
      if (!this.rail) return;

      const DRAG_SLOP = 4; // px before a press counts as a drag, not a click
      let pointerId = null;
      let startY = 0;
      let startScroll = 0;
      let moved = false;

      this.rail.addEventListener('pointerdown', (event) => {
        // Mouse only: touch and pen already scroll natively.
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (this.rail.scrollHeight <= this.rail.clientHeight) return;

        pointerId = event.pointerId;
        startY = event.clientY;
        startScroll = this.rail.scrollTop;
        moved = false;
        this.rail.classList.add('is-grabbing');
      });

      this.rail.addEventListener('pointermove', (event) => {
        if (pointerId === null || event.pointerId !== pointerId) return;

        const delta = event.clientY - startY;
        if (!moved && Math.abs(delta) > DRAG_SLOP) {
          moved = true;
          // Capture only once it's a real drag, so a plain click still lands on
          // the thumbnail button underneath.
          this.rail.setPointerCapture(pointerId);
        }
        if (!moved) return;

        event.preventDefault();
        this.rail.scrollTop = startScroll - delta;
      });

      const end = (event) => {
        if (pointerId === null || (event && event.pointerId !== pointerId)) return;
        if (this.rail.hasPointerCapture?.(pointerId)) this.rail.releasePointerCapture(pointerId);
        pointerId = null;
        this.rail.classList.remove('is-grabbing');

        if (moved) {
          // Swallow the click this drag would otherwise produce.
          this.rail.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
          }, { capture: true, once: true });
        }
      };

      this.rail.addEventListener('pointerup', end);
      this.rail.addEventListener('pointercancel', end);

      // Native image dragging would hijack the gesture.
      this.rail.querySelectorAll('img').forEach((img) => {
        img.setAttribute('draggable', 'false');
      });
    }

    bindRail() {
      if (!this.rail) return;
      this.rail.addEventListener(
        'click',
        (event) => {
          if (!this.embla) return;
          const item = event.target.closest('[data-target]');
          if (!item) return;

          const index = this.slides.findIndex((s) => s.dataset.mediaId === item.dataset.target);
          if (index === -1) return;

          event.preventDefault();
          event.stopPropagation();
          this.embla.scrollTo(index);
        },
        true
      );
    }

    /* ---- state --------------------------------------------------------- */

    sync() {
      const index = this.embla.selectedScrollSnap();

      // .is-active drives the theme's own styling elsewhere, so keep it honest.
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));

      if (this.rail) {
        this.rail.querySelectorAll('button').forEach((b) => b.removeAttribute('aria-current'));
        const id = this.slides[index]?.dataset.mediaId;
        const thumb = id && this.rail.querySelector(`[data-target="${id}"] button`);
        if (thumb) thumb.setAttribute('aria-current', 'true');
        // The rail is hidden on mobile, so scrolling it there is pointless work.
        if (this.isDesktop()) {
          this.syncRailHeight();
          this.scrollRailInto(index);
        }
      }

      this.syncDots(index);

      if (this.prev) this.prev.disabled = !this.embla.canScrollPrev();
      if (this.next) this.next.disabled = !this.embla.canScrollNext();

      this.preloadAround(index);
      this.playActive(index);
    }

    /* Cap the thumbnail rail to the main image's height.
       CSS alone can't do it here: the rail and the viewer are siblings in the
       same grid row, so stretching the rail makes the row (and the whole
       gallery) grow to fit a long strip, and pulling it out of flow collapses
       the gallery's height. Measuring the image and writing --rail-max keeps
       the rail exactly as tall as the photo, scrolling within it. */
    syncRailHeight() {
      if (!this.rail || !this.isDesktop()) return;
      const frame = this.slides[this.embla ? this.embla.selectedScrollSnap() : 0];
      const box = frame?.querySelector('.product-media-container');
      const height = box?.getBoundingClientRect().height;
      if (height) this.rail.style.setProperty('--rail-max', `${Math.round(height)}px`);

      // Show the grab cursor only when the strip really overflows.
      this.rail.classList.toggle('is-draggable', this.rail.scrollHeight > this.rail.clientHeight);
    }

    // Keep the selected thumbnail inside the rail's visible area.
    scrollRailInto(index) {
      const items = this.rail.querySelectorAll('.thumbnail-list__item');
      const item = items[index];
      if (!item) return;

      // The RAIL is the scroll container (see section-main-product.css), not the
      // <ul> inside it.
      const scroller = this.rail;
      if (scroller.scrollHeight <= scroller.clientHeight) return;

      // Measured against the scroller's own box rather than offsetTop: the rail
      // isn't a positioned element, so offsetParent resolves further up the tree
      // and offsetTop would not be the offset inside the scroller.
      const itemBox = item.getBoundingClientRect();
      const box = scroller.getBoundingClientRect();
      const top = itemBox.top - box.top + scroller.scrollTop;
      const bottom = top + itemBox.height;

      if (top < scroller.scrollTop) {
        scroller.scrollTo({ top, behavior: 'smooth' });
      } else if (bottom > scroller.scrollTop + scroller.clientHeight) {
        scroller.scrollTo({ top: bottom - scroller.clientHeight, behavior: 'smooth' });
      }
    }

    // Lazy images inside a carousel don't intersect the viewport until they are
    // already sliding in, which shows a blank frame. Promote the neighbours —
    // same approach as the Featured products gallery.
    preloadAround(index) {
      [index, index + 1, index - 1].forEach((i) => {
        this.slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    }

    playActive(index) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferred = this.slides[index]?.querySelector('.deferred-media');
      if (deferred && typeof deferred.loadContent === 'function') deferred.loadContent(false);
    }
  }

  const init = () => {
    document.querySelectorAll('.new-design-product media-gallery').forEach((gallery) => {
      if (gallery.dataset.ndGallery === 'true') return;
      gallery.dataset.ndGallery = 'true';
      new ProductGallery(gallery);
    });
  };

  // embla-carousel.umd.js is deferred too, so wait for it to be parsed.
  const start = () => {
    if (typeof window.EmblaCarousel === 'undefined') {
      window.requestAnimationFrame(start);
      return;
    }
    init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.new-design-product media-gallery').forEach((g) => {
      delete g.dataset.ndGallery;
    });
    start();
  });
})();
